import * as React from "react";
import Grid from '@mui/material/Grid2';
import { Box, Stack, useTheme } from "@mui/material";
import { GlobalMonitoringStatus, RealTimeMonitoringProps } from "./type";
import { Fragment, useContext, useMemo } from "react";
import RealTimeConnector from "./RealTimeConnector";
import RealTimeItemsLabelContainer from "./RealTimeItemsLabelContainer";
import RealTimeLabel from "./RealTimeLabel";
import RealTimeIconContainer from "./RealTimeIconContainer";
import { useTranslation } from "react-i18next";
import Typography from "@common/components/Typography";
import { displayDateTime } from "@common/utils/localTime.utils";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";

type RealTimeStatusProps = {
  lastUpdated?: number;
  loading?: boolean;
  realTimeMonitoringData: RealTimeMonitoringProps[];
  title: string | React.ReactNode;
};

const RealTimeStatus = (props: RealTimeStatusProps) => {
  const { lastUpdated, loading, realTimeMonitoringData, title } = props;
  const { t } = useTranslation();
  const { detail } = useContext(RoomsRealtimeContext);
  const theme = useTheme();

  const globalStatus: GlobalMonitoringStatus = loading
    ? "loading"
    : detail?.meeting
    ? "meeting"
    : detail?.isPaused
    ? "paused"
    : "default";

  const StatusComputeStyle = useMemo(() => {
    switch (globalStatus) {
      case "paused":
        return {
          color: theme.palette.device.label,
        };
      case "loading":
        return {
          color: theme.palette.grey[500],
        };
      case "meeting":
        return { color: theme.palette.meeting.background };
      default:
        return {
          color: theme.palette.primary.main,
        };
    }
  }, [globalStatus, theme]);

  return (
    <Stack
      direction="column"
      sx={{
        gap: "1rem",
      }}
    >
      {typeof title == "string" ? (
        <Typography size="smaller" sx={[StatusComputeStyle]}>
          {title}
          {globalStatus === "paused" && " : " + t("Paused")}
        </Typography>
      ) : (
        title
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          position: "relative",
        }}
      >
        {realTimeMonitoringData.map((data, index) => {
          return (
            <Fragment key={index}>
              {index !== 0 && <RealTimeConnector status={globalStatus} />}
              <RealTimeItemsLabelContainer sx={data.sx}>
                {data.label && (
                  <RealTimeLabel globalStatus={globalStatus} isContainerTitle>
                    {data.label}
                  </RealTimeLabel>
                )}
                {data.itemsMonitoring.length === 1 && (
                  <>
                    <RealTimeLabel
                      {...data.itemsMonitoring[0]}
                      globalStatus={globalStatus}
                    >
                      {t(data.itemsMonitoring[0].label as string)}
                    </RealTimeLabel>
                    <RealTimeIconContainer
                      {...data.itemsMonitoring[0]}
                      globalStatus={globalStatus}
                    >
                      {data.itemsMonitoring[0].icon}
                    </RealTimeIconContainer>
                  </>
                )}

                {data.itemsMonitoring.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {data.itemsMonitoring.map((item, index) => {
                      return (
                        <Fragment key={index}>
                          {index !== 0 && (
                            <RealTimeConnector status={globalStatus} />
                          )}
                          <Grid
                            container
                            direction="column"
                            sx={{ alignItems: "center" }}
                          >
                            <RealTimeLabel
                              {...item}
                              globalStatus={globalStatus}
                            >
                              {t(item.label as string)}
                            </RealTimeLabel>
                            <RealTimeIconContainer
                              {...item}
                              globalStatus={globalStatus}
                            >
                              {item.icon}
                            </RealTimeIconContainer>
                          </Grid>
                        </Fragment>
                      );
                    })}
                  </Box>
                )}
              </RealTimeItemsLabelContainer>
            </Fragment>
          );
        })}
      </Stack>
      <Typography sx={{ color: "gray" }} size="smaller">
        {lastUpdated &&
          t("Last updated : {{ DATE }}", {
            DATE: displayDateTime(lastUpdated),
          })}
      </Typography>
    </Stack>
  );
};

export default RealTimeStatus;
